import { useState, useCallback } from "react";
import { FiCpu, FiCheck, FiX, FiLoader } from "react-icons/fi";
import type {
	AIReviewResponse,
	AITriageSuggestion,
	AIDuplicateGroup,
	ReviewClarification,
	TriageStatus,
	DeclineReason,
} from "../../../../types/clarification-review.type";
import { AISummaryPanel } from "./AISummaryPanel";
import { AISuggestionRow } from "./AISuggestionRow";
import { AIDuplicateGroupCard } from "./AIDuplicateGroupCard";

interface AIReviewPanelProps {
	rfxId: number;
	clarifications: ReviewClarification[];
	aiReview: AIReviewResponse | null;
	isLoading: boolean;
	onRequestReview: () => void;
	onAcceptSuggestion: (
		clarificationId: number,
		status: TriageStatus,
		declineReason?: string,
		declineCategory?: DeclineReason
	) => void;
	onAcceptDuplicateGroup: (mainId: number, duplicateIds: number[]) => void;
}

export const AIReviewPanel: React.FC<AIReviewPanelProps> = ({
	clarifications,
	aiReview,
	isLoading,
	onRequestReview,
	onAcceptSuggestion,
	onAcceptDuplicateGroup,
}) => {
	const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<number>>(new Set());
	const [acceptedSuggestions, setAcceptedSuggestions] = useState<Set<number>>(new Set());
	const [dismissedDuplicates, setDismissedDuplicates] = useState<Set<number>>(new Set());
	const [acceptedDuplicates, setAcceptedDuplicates] = useState<Set<number>>(new Set());

	const handleAcceptSuggestion = useCallback(
		(suggestion: AITriageSuggestion) => {
			onAcceptSuggestion(
				suggestion.clarification_id,
				suggestion.suggested_status,
				suggestion.suggested_status === "declined" ? suggestion.reasoning : undefined,
				suggestion.suggested_status === "declined" ? "other" : undefined
			);
			setAcceptedSuggestions((prev) => new Set(prev).add(suggestion.clarification_id));
		},
		[onAcceptSuggestion]
	);

	const handleDismissSuggestion = useCallback((clarificationId: number) => {
		setDismissedSuggestions((prev) => new Set(prev).add(clarificationId));
	}, []);

	const handleAcceptDuplicateGroup = useCallback(
		(group: AIDuplicateGroup) => {
			onAcceptDuplicateGroup(group.suggested_main_id, group.duplicate_ids);
			setAcceptedDuplicates((prev) => new Set(prev).add(group.suggested_main_id));
		},
		[onAcceptDuplicateGroup]
	);

	const handleDismissDuplicateGroup = useCallback((mainId: number) => {
		setDismissedDuplicates((prev) => new Set(prev).add(mainId));
	}, []);

	const handleBulkAcceptSuggestions = useCallback(() => {
		if (!aiReview) {
			return;
		}
		for (const suggestion of aiReview.suggestions) {
			if (!dismissedSuggestions.has(suggestion.clarification_id) && !acceptedSuggestions.has(suggestion.clarification_id)) {
				handleAcceptSuggestion(suggestion);
			}
		}
	}, [aiReview, dismissedSuggestions, acceptedSuggestions, handleAcceptSuggestion]);

	const handleBulkDismissSuggestions = useCallback(() => {
		if (!aiReview) {
			return;
		}
		const newDismissed = new Set(dismissedSuggestions);
		for (const suggestion of aiReview.suggestions) {
			if (!acceptedSuggestions.has(suggestion.clarification_id)) {
				newDismissed.add(suggestion.clarification_id);
			}
		}
		setDismissedSuggestions(newDismissed);
	}, [aiReview, dismissedSuggestions, acceptedSuggestions]);

	// Pre-review state
	if (!aiReview && !isLoading) {
		return (
			<div className="rounded-lg border border-dashed border-indigo-300 bg-indigo-50/50 p-4" data-testid="ai-review-panel">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<FiCpu className="text-indigo-500" size={18} />
						<div>
							<p className="text-sm font-medium text-indigo-800">AI Review Assistant</p>
							<p className="text-xs text-indigo-600">
								Get AI-powered triage suggestions, duplicate detection, and review summary.
							</p>
						</div>
					</div>
					<button
						type="button"
						className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
						onClick={onRequestReview}
						data-testid="ai-review-trigger"
					>
						<FiCpu size={14} />
						AI Review
					</button>
				</div>
			</div>
		);
	}

	// Loading state
	if (isLoading) {
		return (
			<div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4" data-testid="ai-review-loading">
				<div className="flex items-center gap-3">
					<FiLoader className="animate-spin text-indigo-600" size={18} />
					<div>
						<p className="text-sm font-medium text-indigo-800">AI is reviewing clarifications...</p>
						<p className="text-xs text-indigo-600">
							This may take a moment. You can continue reviewing manually while waiting.
						</p>
					</div>
				</div>
			</div>
		);
	}

	// Results state
	const pendingSuggestions = aiReview.suggestions.filter(
		(s) => !dismissedSuggestions.has(s.clarification_id) && !acceptedSuggestions.has(s.clarification_id)
	);
	const pendingDuplicates = aiReview.duplicates.filter(
		(d) => !dismissedDuplicates.has(d.suggested_main_id) && !acceptedDuplicates.has(d.suggested_main_id)
	);

	return (
		<div className="space-y-4" data-testid="ai-review-panel">
			{/* Summary */}
			<AISummaryPanel summary={aiReview.summary} />

			{/* Triage suggestions */}
			{aiReview.suggestions.length > 0 && (
				<div data-testid="ai-suggestions-section">
					<div className="mb-2 flex items-center justify-between">
						<div className="flex items-center gap-2">
							<FiCpu className="text-indigo-500" size={14} />
							<span className="text-sm font-medium text-gray-700">
								Triage Suggestions ({aiReview.suggestions.length})
							</span>
							{pendingSuggestions.length > 0 && (
								<span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs text-indigo-700">
									{pendingSuggestions.length} pending
								</span>
							)}
						</div>
						{pendingSuggestions.length > 1 && (
							<div className="flex gap-1">
								<button
									type="button"
									className="inline-flex items-center gap-1 rounded-md border border-green-300 bg-white px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50"
									onClick={handleBulkAcceptSuggestions}
									data-testid="ai-bulk-accept"
								>
									<FiCheck size={12} />
									Accept All
								</button>
								<button
									type="button"
									className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-50"
									onClick={handleBulkDismissSuggestions}
									data-testid="ai-bulk-dismiss"
								>
									<FiX size={12} />
									Dismiss All
								</button>
							</div>
						)}
					</div>
					<div className="space-y-2">
						{aiReview.suggestions.map((suggestion) => {
							const clarification = clarifications.find(
								(c) => c.id === suggestion.clarification_id
							);
							return (
								<AISuggestionRow
									key={suggestion.clarification_id}
									suggestion={suggestion}
									currentStatus={clarification?.triage_status ?? null}
									referenceNumber={clarification?.reference_number ?? 0}
									onAccept={handleAcceptSuggestion}
									onDismiss={handleDismissSuggestion}
									isDismissed={dismissedSuggestions.has(suggestion.clarification_id)}
									isAccepted={acceptedSuggestions.has(suggestion.clarification_id)}
								/>
							);
						})}
					</div>
				</div>
			)}

			{/* Duplicate groups */}
			{aiReview.duplicates.length > 0 && (
				<div data-testid="ai-duplicates-section">
					<div className="mb-2 flex items-center gap-2">
						<FiCpu className="text-indigo-500" size={14} />
						<span className="text-sm font-medium text-gray-700">
							Duplicate Groups ({aiReview.duplicates.length})
						</span>
						{pendingDuplicates.length > 0 && (
							<span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs text-indigo-700">
								{pendingDuplicates.length} pending
							</span>
						)}
					</div>
					<div className="space-y-2">
						{aiReview.duplicates.map((group) => (
							<AIDuplicateGroupCard
								key={group.suggested_main_id}
								group={group}
								clarifications={clarifications}
								onAccept={handleAcceptDuplicateGroup}
								onDismiss={handleDismissDuplicateGroup}
								isDismissed={dismissedDuplicates.has(group.suggested_main_id)}
								isAccepted={acceptedDuplicates.has(group.suggested_main_id)}
							/>
						))}
					</div>
				</div>
			)}

			{/* Empty results */}
			{aiReview.suggestions.length === 0 && aiReview.duplicates.length === 0 && (
				<div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
					<p className="text-sm text-gray-500">
						AI analysis complete. No additional triage suggestions or duplicate groups detected.
					</p>
				</div>
			)}
		</div>
	);
};
